/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartComponent, IAddToCartResources, OrderDetailsProduct } from '@msdyn365-commerce/components';
import { getUrlSync, IComponentProps, ICoreContext } from '@msdyn365-commerce/core';
import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

/**
 * The data which the component should operate.
 */
export interface IBuySelectedData {
    products: OrderDetailsProduct[];
}

/**
 * The resources needed to render a buy selected component.
 */
export interface IBuySelectedResources {
    orderDetailsUnavailableProductText?: string;
    orderDetailsBuySelectedButtonText?: string;
    orderDetailsBuySelectedButtonAriaLabel?: string;
    orderDetailsBuySelectedAddingToCartErrorNotificationTitle?: string;
    orderDetailsBuySelectedAddingToCartErrorNotificationCloseAriaLabel?: string;
    orderDetailsBuyItAgainLabel?: string;
    orderDetailsBuyItAgainAriaLabel?: string;
    orderDetailsGoToCartText?: string;
    orderDetailsDialogCloseText?: string;
    orderDetailsSingleItemText?: string;
    orderDetailsMultiLineItemFormatText?: string;
    orderDetailsMultiLinesFormatText?: string;
    orderDetailsHeaderMessageText?: string;
    orderDetailsMultiItemsValidationErrorMessage?: string;
    orderDetailsOneErrorText?: string;
    orderDetailsMultiErrorText?: string;
    addedQuantityText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    freePriceText?: string;
}

/**
 * Configuration of a buy selected component.
 */
export interface IBuySelectedProps extends IComponentProps<IBuySelectedData> {
    className: string;
    context: ICoreContext;

    resources: IBuySelectedResources;

    isIcon?: boolean;
    isReorderingEnabled?: boolean;
}

/**
 * Get the dialog strings.
 * @param props -- The buy selected props.
 * @returns The add to cart resources.
 */
export const getDialogStrings = (props: IBuySelectedProps): IAddToCartResources => {
    return {
        goToCartText: props.resources.orderDetailsGoToCartText ?? '',
        continueShoppingText: props.resources.orderDetailsDialogCloseText ?? '',
        closeNotificationLabel: props.resources.orderDetailsBuySelectedAddingToCartErrorNotificationCloseAriaLabel ?? '',
        headerItemOneText: props.resources.orderDetailsSingleItemText ?? '',
        headerItemFormatText: props.resources.orderDetailsMultiLineItemFormatText ?? '',
        headerLinesFormatText: props.resources.orderDetailsMultiLinesFormatText ?? '',
        headerMessageText: props.resources.orderDetailsHeaderMessageText ?? '',
        freePriceText: props.resources.freePriceText ?? '',
        originalPriceText: props.resources.originalPriceText ?? '',
        currentPriceText: props.resources.currentPriceText ?? '',
        addedQuantityText: props.resources.addedQuantityText ?? '',
        buyAgainNotificationTitle: props.resources.orderDetailsBuySelectedAddingToCartErrorNotificationTitle ?? '',
        buyAgainNotificationCloseAriaLabel: props.resources.orderDetailsBuySelectedAddingToCartErrorNotificationCloseAriaLabel ?? '',
        validationErrorMessage: props.resources.orderDetailsMultiItemsValidationErrorMessage,
        oneErrorText: props.resources.orderDetailsOneErrorText,
        multiErrorsText: props.resources.orderDetailsMultiErrorText
    };
};

/**
 * Renders a button which adds given products to the cart.
 * @param props - Configuration of the component.
 * @returns A react element or null if no products were given.
 */
export const BuySelectedComponent: React.FC<IBuySelectedProps> = (props: IBuySelectedProps): JSX.Element | null => {
    const products = props.data.products.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
    const buttonLabel = props.isIcon
        ? ''
        : props.resources.orderDetailsBuySelectedButtonText ?? props.resources.orderDetailsBuyItAgainLabel;
    const buttonAriaLabel = props.resources.orderDetailsBuySelectedButtonAriaLabel ?? props.resources.orderDetailsBuyItAgainAriaLabel;

    const singleProductCount = 1;
    if (props.data.products.length === singleProductCount && !props.data.products[0].isProductAvailable) {
        // Return disabled button
        return (
            <button
                className={props.className}
                aria-label={props.resources.orderDetailsUnavailableProductText}
                title={props.resources.orderDetailsUnavailableProductText}
                disabled
            >
                {buttonLabel}
            </button>
        );
    }

    if (!ArrayExtensions.hasElements(products)) {
        // Return disabled button
        return (
            <button className={props.className} aria-label={buttonAriaLabel} title={buttonAriaLabel} disabled>
                {buttonLabel}
            </button>
        );
    }

    const firstProduct: OrderDetailsProduct = products[0];

    // Reorder is not enable, buy again button link to product details page.
    if (!props.isReorderingEnabled) {
        return (
            <Button className={props.className} aria-label={buttonAriaLabel} title={buttonAriaLabel} href={firstProduct.productUrl}>
                {buttonLabel}
            </Button>
        );
    }

    const shouldSkipSiteSettings: boolean = true;
    const dialogStrings: IAddToCartResources = getDialogStrings(props);

    return (
        <AddToCartComponent
            addToCartText={buttonLabel ?? ''}
            outOfStockText=''
            addToCartArialLabel={buttonAriaLabel}
            quantity={firstProduct.salesLine.Quantity}
            id={props.id}
            typeName={props.typeName}
            className={props.className}
            navigationUrl={getUrlSync('cart', props.context.actionContext)}
            data={{ product: firstProduct.simpleProduct! }}
            context={props.context}
            dialogStrings={dialogStrings}
            shouldSkipSiteSettings={shouldSkipSiteSettings}
            orderDetailsProducts={products}
        />
    );
};
